// Decide modal — pass / invest / mark reviewed, persisted to the backend.

import { useEffect, useState } from "react";
import { useAppData, useMe } from "../store";
import {
  Avatar, Btn, Chip, IconBtn, TextArea, TextInput, Toggle,
} from "../components/Primitives";
import { Ic } from "../components/Icon";

export type DecideKind = "pass" | "invest" | "review";

interface Props {
  id: string;
  kind: DecideKind;
  onClose: () => void;
  onSaved: () => void;
}

export function DecideModal({ id, kind, onClose, onSaved }: Props) {
  const { data, decideDeal } = useAppData();
  const me = useMe();
  const deal = data?.deals.find((x) => x.id === id) ?? data?.archive.find((x) => x.id === id);

  const [reasons, setReasons] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [conviction, setConviction] = useState(3);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultRound = deal?.fields?.["Round size"]?.value as string | undefined;
  const [round, setRound] = useState(defaultRound ?? "$2.5M");
  const [check, setCheck] = useState("$500K");
  const [val,   setVal]   = useState("$22M cap");
  const [lead,  setLead]  = useState(false);
  const [board, setBoard] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!deal || !data) return null;

  const titles: Record<DecideKind, { t: string; s: string }> = {
    pass:   { t: `Pass on ${deal.co}`,    s: "Capture the reasons. We'll look back at this." },
    invest: { t: `Invest in ${deal.co}`,  s: "Record terms and conviction at the time of decision." },
    review: { t: `Mark ${deal.co} as reviewed`, s: "Add a note so the team knows where this stands." },
  };
  const info = titles[kind];

  const toggleReason = (r: string) =>
    setReasons((rs) => rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await decideDeal(id, {
        kind,
        reasonTags: kind === "pass" ? reasons : undefined,
        reason: kind !== "review" ? note : undefined,
        conviction: kind === "review" ? undefined : conviction,
        terms: kind === "invest"
          ? { round, check, valuation: val, lead, board } : undefined,
      });   // the backend stamps decidedBy from the session
      onSaved();
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <div className="modal-head">
          <div className="who">
            <div className="t">{info.t}</div>
            <div className="s">{info.s}</div>
          </div>
          <IconBtn ico="x" onClick={onClose} label="Close" />
        </div>

        <div className="modal-body">
          {kind === "pass" && (
            <>
              <div>
                <label className="field-label">Reason</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {data.config.passReasons.map((r) => (
                    <Chip key={r} on={reasons.includes(r)} onClick={() => toggleReason(r)}>
                      {r}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">Notes for the archive</label>
                <TextArea
                  placeholder="A sentence or two on why now isn't the time."
                  value={note} onChange={setNote}
                />
              </div>
            </>
          )}

          {kind === "invest" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <label className="field-label">Round size</label>
                  <TextInput value={round} onChange={setRound} />
                </div>
                <div>
                  <label className="field-label">Our check</label>
                  <TextInput value={check} onChange={setCheck} />
                </div>
                <div>
                  <label className="field-label">Valuation</label>
                  <TextInput value={val} onChange={setVal} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <Toggle on={lead} onChange={setLead} />
                  <span style={{ fontSize: 13 }}>We're leading the round</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <Toggle on={board} onChange={setBoard} />
                  <span style={{ fontSize: 13 }}>We're taking a board seat</span>
                </div>
              </div>
              <div>
                <label className="field-label">Notes for the archive</label>
                <TextArea
                  placeholder="What you believe is true that others might not."
                  value={note} onChange={setNote}
                />
              </div>
            </>
          )}

          {kind === "review" && (
            <div>
              <label className="field-label">Note</label>
              <TextArea
                placeholder="A line on what you're waiting for, who else is looking, or what to dig into next."
                value={note} onChange={setNote}
              />
            </div>
          )}

          {kind !== "review" && (
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 20, alignItems: "center" }}>
              <div>
                <label className="field-label">Conviction at decision</label>
                <div className="conviction">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span
                      key={n}
                      className={"step" + (n === conviction ? " on" : "")}
                      onClick={() => setConviction(n)}
                    >{n}</span>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">Decision maker</label>
                <div className="input" style={{ maxWidth: 220 }}>
                  <Avatar id={me?.id} size="sm" />
                  <span style={{ fontSize: 13 }}>{me?.name ?? "—"}</span>
                  <span style={{ marginLeft: "auto", color: "var(--ink-3)" }}>
                    <Ic name="chevDown" size={12} />
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={{
              color: "var(--neg)", background: "var(--neg-soft)",
              padding: "8px 12px", borderRadius: 6, fontSize: 12.5,
            }}>{error}</div>
          )}
        </div>

        <div className="modal-foot">
          {kind === "pass" && (
            <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
              This decision lives in the archive forever — including in the anti-portfolio if the company makes it.
            </span>
          )}
          <span className="grow" />
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind={kind === "invest" ? "accent" : "primary"} onClick={save} disabled={saving}>
            {saving ? "Saving…" : kind === "pass" ? "Pass deal" : kind === "invest" ? "Record investment" : "Mark reviewed"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
